import styled from '@emotion/styled'
import { Dropdown } from '@leshen/ui'
import { Link as GatsbyLink, graphql, navigate, withPrefix } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import React, { useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import GlobalCss from '../components/CustomStyles/GlobalStyles'
import footer from '../components/Footer'
import header from '../components/Header'
import SeoCtaBar from '../components/SeoCtaBar'

import {
  Accordion,
  AvailabilityForm,
  Billboard,
  Brandy,
  Column,
  Columns,
  LinkButton,
  List,
  ListItem,
  PackageCard,
  SplitContent,
  Stack,
  Typography,
  useMapi,
  VariableContent,
} from '@leshen/gatsby-theme-leshen'
import { Done, Phone } from 'emotion-icons/material'

import { Layout } from '@leshen/gatsby-theme-contentful'

import PhoneCTA from '@leshen/gatsby-theme-leshen/src/components/PhoneCTA'
import siteTheme from 'gatsby-theme-dish/theme'

const City = ({ data }) => {
  const [isCaliforniaPage, setIsCaliforniaPage] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.location.pathname.includes('/availability/ca')) {
        setIsCaliforniaPage(true)
      } else {
        setIsCaliforniaPage(false)
      }
    }
  }, [])

  const {
    Meta_Description,
    Meta_Title,
    Page_Path,
    City,
    State,
    FAQ_1,
    FAQ_2,
    FAQ_3,
    FAQ_4,
    FAQ_5,
    FAQ_6,
    Promo_Code,
    Customer_Service,
  } = data.pageInfo.edges[0].node
  const { rotatedNumber } = useMapi()

  const getFAQ = (shortName) => {
    const faq = {
      QA1: {
        question: `Is DISH Network offered in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH Network is offered in {City}, {State} as well as the other 49
            states in the U.S. Call to see if it’s available in your
            neighborhood.
          </Typography>
        ),
      },
      QA2: {
        question: `Does ${City}, ${State} offer DISH Satellite TV services? `,
        answer: (
          <Typography variant="feature">
            {City}, {State} offers DISH satellite TV services, including sports
            packages and local programming. Find out if DISH satellite TV is in
            your area today.
          </Typography>
        ),
      },
      QA3: {
        question: `Is DISH TV available near ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH TV is available in {City}, {State}. From sports packages to
            local programming, DISH TV offers multiple entertainment options.
            Find out if DISH TV is offered at your address today.
          </Typography>
        ),
      },
      QA4: {
        question: `Is DISH Network available in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH Network is available in {City}, {State}. Find out if DISH
            Network is available at your address by giving us a call.
          </Typography>
        ),
      },
      QP1: {
        question: `What is the cost of DISH Satellite TV in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH Satellite TV costs vary by package size. In {City}, {State},
            packages start at $91.99/mo.for the basic America’s Top 120 package.
            Give us a call to learn more about package prices.
          </Typography>
        ),
      },
      QP2: {
        question: `How much are DISH Network packages in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH Network packages in {City}, {State} start at $91.99/mo. for the
            basic America’s Top 120 package. Call to learn more about the
            additional TV packages DISH offers.
          </Typography>
        ),
      },
      QP3: {
        question: `How much does satelllite tv service cost in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            Satellite TV service from DISH starts at $91.99/mo. in {City},
            {State}. Prices vary depending on add-ons like the sports package or
            the DISH Movie Pack. Call for more details.
          </Typography>
        ),
      },
      QP4: {
        question: `What is the price of satellite TV in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH satellite TV prices in {City}, {State} start at $91.99/mo.
            depending on the package you choose. Call to find out what packages
            are available in your area.
          </Typography>
        ),
      },
      QC1: {
        question: `What is the number to contact DISH Network in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            The number to contact DISH Network in {City}, {State} is{' '}
            <a href={'tel:833-871-4427'}>833-871-4427</a>.
          </Typography>
        ),
      },
      QC2: {
        question: `What is the phone number to get DISH TV service in ${City}, ${State}`,
        answer: (
          <Typography variant="feature">
            The phone number to get DISH TV service in {City}, {State} is{' '}
            <a href={'tel:833-871-4427'}>833-871-4427</a>.
          </Typography>
        ),
      },
      QC3: {
        question: `How can I contact DISH satellite TV in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            You can contact DISH satellite TV by calling{' '}
            <a href={'tel:833-871-4427'}>833-871-4427</a>.
          </Typography>
        ),
      },
      QC4: {
        question: `How do I contact DISH Network in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            Call <a href={'tel:833-871-4427'}>833-871-4427</a> to contact DISH
            Network in {City}, {State}.
          </Typography>
        ),
      },
      QB1: {
        question: `How much do DISH TV and internet packages in ${City}, ${State} cost?`,
        answer: (
          <Typography variant="feature">
            DISH TV and internet packages in {City}, {State} vary in pricing.
            Your monthly bill will vary depending on which internet provider you
            go with and the TV package that works for you. Call and find out how
            much it would cost to{' '}
            <GatsbyLink to="https://www.usdish.com/bundles">
              bundle your internet and TV
            </GatsbyLink>{' '}
            today!
          </Typography>
        ),
      },
      QB2: {
        question: `Does DISH Satellite TV offer bundles with internet in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH Satellite TV offers bundles with internet in {City}, {State}.
            Depending on where you live, it can be satellite internet with
            HughesNet or fiber internet with Frontier. Either way, you can have
            the best of both worlds when you{' '}
            <GatsbyLink to="https://www.usdish.com/bundles">
              bundle DISH TV with internet
            </GatsbyLink>
            .
          </Typography>
        ),
      },
      QB3: {
        question: `Can I bundle internet with DISH TV in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            You can{' '}
            <GatsbyLink to="https://www.usdish.com/bundles">
              bundle internet with DISH TV
            </GatsbyLink>{' '}
            in {City}, {State}. Call to find out which internet providers are in
            your area and which TV package works best for you.
          </Typography>
        ),
      },
      QB4: {
        question: `Can I combine internet services with DISH TV in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            Yes, you can indeed{' '}
            <GatsbyLink to="https://www.usdish.com/bundles">
              bundle internet services with DISH TV
            </GatsbyLink>{' '}
            in your state, depending on the availability of internet providers
            partnered with DISH. These partners often include HughesNet
            Satellite Internet and Frontier, providing high-speed internet
            access across various regions.
          </Typography>
        ),
      },
      QD1: {
        question: `What type of satellite TV packages are offered in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH offers three satellite TV packages in {City}, {State}:
            America&#39;s Top 120-190 channels, including 60+ in high definition;
            America&#39;s Top 200-240+ channels, including 125+ in high definition;
            America&#39;s Top 250-290+ channels, including 140+ in high definition.
          </Typography>
        ),
      },
      QD2: {
        question: `What type of deals does DISH TV offer near ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH TV offers tons of deals near {City}, {State}, including a
            2-year TV price guarantee, free standard professional installation,
            and free premium channels for three months, just to name a few.
          </Typography>
        ),
      },
      QD3: {
        question: `What deals are available for DISH in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            A 2-year price guarantee, free standard professional installation,
            and a free DISH Anywhere mobile app are just a few of the deals
            available with DISH in {City}, {State}. Call to learn more about all
            the deals DISH has to offer.
          </Typography>
        ),
      },
      QD4: {
        question: `Does DISH Network offer any deals in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH Network offers a 2-year price guarantee, free premium channels
            for three months, and free HD for life in {City}, {State}, just to
            name a few. Call us to learn more about DISH deals in your area.
          </Typography>
        ),
      },
      QI1: {
        question: `Is DISH internet available in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH Network works with internet providers such as HughesNet and
            Frontier to connect you to the internet in {City}, {State}. Call us
            to find out if DISH offers{' '}
            <GatsbyLink to="https://www.usdish.com/bundles">
              internet bundles in your area
            </GatsbyLink>
            .
          </Typography>
        ),
      },
      QI2: {
        question: `Does DISH Network offer internet in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            Satellite TV service from DISH starts at $91.99/mo. in {City},
            {State}. Prices vary depending on add-ons like the sports package or
            the DISH Movie Pack. Call for more details.
          </Typography>
        ),
      },
      QI3: {
        question: `Can I get internet with DISH in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            You can get internet with one of DISH’s internet partners in {City},{' '}
            {State}. Find out if DISH has internet partners serving your address
            by giving us a call.
          </Typography>
        ),
      },
      QI4: {
        question: `Does DISH have satellite internet in ${City}, ${State}?`,
        answer: (
          <Typography variant="feature">
            DISH works with Hughesnet to provide satellite internet in rural
            areas. DISH also works with Frontier to connect customers to the
            internet through a fiber network. Find out which internet provider
            services your area by giving us a call.
          </Typography>
        ),
      },
    }
    return faq[shortName]
  }

  const faqSchemaData = (shortName, piece) => {
    const schemaFAQ = {
      QA1: {
        question: `Is DISH Network offered in ${City}, ${State}?`,
        answer: `DISH Network is offered in ${City}, ${State} as well as the other 49
        states in the U.S. Call to see if it’s available in your
        neighborhood.`,
      },
      QA2: {
        question: `Does ${City}, ${State} offer DISH Satellite TV services?`,
        answer: `${City}, ${State} offers DISH satellite TV services, including
        sports packages and local programming. Find out if DISH satellite TV
        is in your area today.`,
      },
      QA3: {
        question: `Is Dish TV available near ${City}, ${State}?`,
        answer: `DISH TV is available in ${City}, ${State}. From sports packages to
        local programming, DISH TV offers multiple entertainment options.
        Find out if DISH TV is offered at your address today.`,
      },
      QA4: {
        question: `Is DISH Network availabile in ${City}, ${State}?`,
        answer: `DISH Network is available in ${City}, ${State}. Find out if DISH Network is available at your address by giving us a call.`,
      },
      QP1: {
        question: `What is the cost of DISH Satellite TV in ${City}, ${State}?`,
        answer: `DISH Satellite TV costs vary by package size. In ${City}, ${State},
        packages start at $91.99/mo.for the basic America’s Top 120 package.
        Give us a call to learn more about package prices.`,
      },
      QP2: {
        question: `How much are DISH Network packages in ${City}, ${State}?`,
        answer: `DISH Network packages in ${City}, ${State} start at $91.99/mo. for the
        basic America’s Top 120 package. Call to learn more about the
        additional TV packages DISH offers.`,
      },
      QP3: {
        question: `How much does satelllite TV service cost in ${City}, ${State}?`,
        answer: `Satellite TV service from DISH starts at $91.99/mo. in ${City}, ${State}. Prices vary depending on add-ons like the sports package or
        the DISH Movie Pack. Call for more details.`,
      },
      QP4: {
        question: `What is the price of satellite TV in ${City}, ${State}?`,
        answer: `DISH satellite TV prices in ${City}, ${State} start at $91.99/mo.
        depending on the package you choose. Call to find out what packages
        are available in your area.`,
      },
      QC1: {
        question: `What is the number to contact DISH Network in ${City}, ${State}?`,
        answer: `The number to contact DISH Network in ${City}, ${State} is <a href={'tel:833-871-4427'}>833-871-4427</a>`,
      },
      QC2: {
        question: `What is the phone number to get DISH TV service in ${City}, ${State}?`,
        answer: `The phone number to get DISH TV service in ${City}, ${State} is <a href={'tel:833-871-4427'}>833-871-4427</a>.`,
      },
      QC3: {
        question: `How can I contact DISH TV satellite TV in ${City}, ${State}?`,
        answer: 'You can contact DISH satellite TV by calling <a href={\'tel:833-871-4427\'}>833-871-4427</a>.',
      },
      QC4: {
        question: `How do I contact DISH Network in ${City}, ${State}?`,
        answer: `Call <a href={'tel:833-871-4427'}>833-871-4427</a> to contact
        DISH Network in ${City}, ${State}.`,
      },
      QB1: {
        question: `How much do DISH TV and internet packages in ${City}, ${State} cost?`,
        answer: `DISH TV and internet packages in ${City}, ${State} vary in pricing. Your monthly bill will vary depending on which internet provider you go with and the TV package that works for you. Call and find out how much it would be to <a href='https://www.usdish.com/bundles'>bundle your internet and TV</a> today!`,
      },
      QB2: {
        question: `Does DISH Satellite TV offer bundles with internet in ${City}, ${State}?`,
        answer: `DISH satellite TV offers bundles with internet in ${City}, ${State}. Depending on where you live, it can be satellite internet with Hughesnet or fiber internet with Frontier. Either way, you can have the best of both worlds when you bundle DISH TV with internet.`,
      },
      QB3: {
        question: `Can I bundle internet with DISH TV in ${City}, ${State}?`,
        answer: `You can bundle internet with DISH TV in ${City}, ${State}. Call to find out which internet providers are in your area and which TV package works best for you.`,
      },
      QB4: {
        question: `Can I combine internet services with DISH TV in ${City}, ${State}?`,
        answer: 'Yes, you can indeed bundle internet services with DISH TV in your state, depending on the availability of internet providers partnered with DISH. These partners often include HughesNet Satellite Internet and Frontier, providing high-speed internet access across various regions.',
      },
      QD1: {
        question: `What type of satellite TV packages are offered in ${City}, ${State}?`,
        answer: `DISH offers four satellite TV packages in ${City}, ${State}: America's Top 120 - 190 channels, including 60+ in high definition; America's Top 200 - 240+ channels, including 125+ in high definition; America's Top 250 - 290+ channels, including 140+ in high definition`,
      },
      QD2: {
        question: `What type of deals does DISH TV offer near ${City}, ${State}?`,
        answer: `DISH TV offers tons of deals near ${City}, ${State}, including a 2-year TV price guarantee, free standard professional installation, and free premium channels for three months, just to name a few.`,
      },
      QD3: {
        question: `What deals are available for DISH in ${City}, ${State}?`,
        answer: `A 2-year price guarantee, free standard professional installation, and a free DISH Anywhere mobile app are just a few of the deals available with DISH in ${City}, ${State}. Call to learn more about all the deals DISH has to offer.`,
      },
      QD4: {
        question: `Does DISH Network offer any deals in ${City}, ${State}?`,
        answer: `DISH Network offers a 2-year price guarantee, free premium channels for three months, and free HD for life in ${City}, ${State}, just to name a few. Call us to learn more about DISH deals in your area.`,
      },
      QI1: {
        question: `Is DISH internet available in ${City}, ${State}?`,
        answer: `DISH Network works with internet providers such as Hughesnet and Frontier to connect you to the internet in ${City}, ${State}. Call us and find out if DISH offers internet bundles in your area.`,
      },
      QI2: {
        question: `Does DISH Network offer internet in ${City}, ${State}?`,
        answer: `Satellite TV service from DISH starts at $91.99/mo. in ${City}, ${State}. Prices vary depending on add-ons like the sports package or the DISH Movie Pack. Call for more details.`,
      },
      QI3: {
        question: `Does DISH Network offer internet in ${City}, ${State}?`,
        answer: `You can get internet with one of DISH’s internet partners in ${City}, ${State}. Find out if DISH has internet partners serving your address by giving us a call.`,
      },
      QI4: {
        question: `Does DISH have satellite internet in ${City}, ${State}?`,
        answer: 'DISH works with Hughesnet to provide satellite internet in rural areas. DISH also works with Frontier to connect customers to the internet through a fiber network. Find out which internet provider services your area by giving us a call.',
      },
    }
    return schemaFAQ?.[shortName]?.[piece]
  }
  const generateSchema = () => {
    const schema = `
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_1, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_1, 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_2, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_2, 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_3, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_3, 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_4, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_4, 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_5, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_5, 'answer')}"
            }
          },
          {
            "@type": "Question",
            "name": "${faqSchemaData(FAQ_6, 'question')}",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "${faqSchemaData(FAQ_6, 'answer')}"
            }
          }
        ]
      }
      `
    return schema
  }

  const brandyPackageList = [
    'dn-americastop120',
    'dn-americastop200',
    'dn-americastop250',
  ]

  const packageList = brandyPackageList
    .map((packageId) =>
      data.allBrandyPackage.edges.find(
        ({ node }) => node.brandy_id === packageId,
      ),
    )
    .filter((node) => node)

  const cityDropdownData = data.cities.edges.map((current) => ({
    label: current.node.City,
    value: current.node.Page_Path,
  }))

  const layoutProps = {
    data: {
      contentfulPage: {
        header: header(),
        footer: footer(),
        seo: {
          title: Meta_Title,
          description: Meta_Description,
          canonical: `https://www.dish.com/availability${Page_Path}`,
          robots: 'index, follow',
        },
        path: Page_Path,
        promoCode: Promo_Code,
        spanish: false,
      },
      site: {
        siteMetadata: {
          siteURL: 'https://www.dish.com/availability',
          siteName: 'Dish',
          alternateName: 'Dish',
          trailingSlash: 'never',
        },
      },
    },
    titleTemplate: Meta_Title,
    defaultRobots: 'follow,index',
    main: (
      <>
        <Helmet>
          <script type="application/ld+json">{`${generateSchema()}`}</script>
        </Helmet>
        <GlobalCss />
        <Billboard
          backgroundColor="midgray"
          variant="split"
          image={
            <StaticImage
              loading="eager"
              src="../images/lifestyle/family-watching-show.webp"
              alt="Family watching tv"
              placeholder="blurred"
            />
          }
          mobileImage={
            <StaticImage
              loading="eager"
              src="../images/lifestyle/family-watching-show.webp"
              alt="Family watching tv"
              placeholder="blurred"
            />
          }
          mainContent={
            <>
              <Typography variant="h1">
                DISH Network in {City}, {State}
              </Typography>
              <Typography variant="h4">
                <strong>Find DISH Satellite TV near your area today!</strong>
              </Typography>
              <List>
                <ListItem icon={<Done />}>
                  <Typography>Local channels</Typography>
                </ListItem>
                <ListItem icon={<Done />}>
                  <Typography>Sports packages</Typography>
                </ListItem>
                <ListItem icon={<Done />}>
                  <Typography>Endless HD entertainment</Typography>
                </ListItem>
              </List>
              <br />
              {isCaliforniaPage ? (
                <PhoneCTA color="primary" staticPhoneNumber="855-662-4704">
                  Call to Learn More!
                </PhoneCTA>
              ) : (
                <PhoneCTA color="primary">Call to Learn More!</PhoneCTA>
              )}
            </>
          }
        />

        <SeoCtaBar />

        <VariableContent
          style={{ backgroundColor: siteTheme.colors.light }}
          mainContent={
            <>
              <Typography variant="h2">
                DISH SATELLITE TV PACKAGES IN YOUR CITY
              </Typography>
              <Typography variant="body">
                Find the best DISH packages in {City}, {State} featuring the
                channels you want. And with a 2-year price guarantee, you’ll
                never pay more than expected for 24 months.
              </Typography>
            </>
          }
        >
          <Stack spacing="xxl">
            <Columns>
              {packageList.map((currentPackage) => (
                <PackageCard
                  packageData={{ ...currentPackage.node }}
                  key={currentPackage.node.brandy_id}
                  content={
                    <>
                      {isCaliforniaPage ? (
                        <PhoneCTA
                          color="primary"
                          staticPhoneNumber="855-662-4704"
                        >
                          Call
                        </PhoneCTA>
                      ) : (
                        <PhoneCTA color="primary">Call</PhoneCTA>
                      )}
                    </>
                  }
                />
              ))}
            </Columns>
            <Brandy
              text={data.disclaimerTwo.text}
              variant="legal"
              style={{
                textAlign: 'center',
              }}
            />
            <Brandy
              text={data.disclaimerThree.text}
              variant="legal"
              style={{
                textAlign: 'center',
                margin: 'auto',
              }}
            />
          </Stack>
        </VariableContent>

        <VariableContent
          mainContent={
            <>
              <Typography variant="h2" className="uppercase">
                MOVING TO THE {City} AREA?{' '}
              </Typography>
              <Typography variant="body">
                DISH is ready to dive in and help you get settled into your new
                home with the right TV package.
              </Typography>
            </>
          }
        >
          <Stack>
            <Columns
              className="zip-form-box center-text info-cards"
              leftAligned={false}
            >
              <Column
                style={{ paddingTop: '20px' }}
                image={
                  <StaticImage
                    style={{ marginBottom: '16px' }}
                    src="../images/icons/tv-remote.svg"
                    alt="tv remote"
                    placeholder="blurred"
                  />
                }
              >
                <Typography variant="h4">
                  <strong>Existing DISH customer?</strong>
                </Typography>
                <Typography className="center-text" variant="body">
                  We’ll help you move your existing services to your new place
                  in no time.
                </Typography>
                <LinkButton
                  to={`tel:${rotatedNumber}`}
                  target="_blank"
                  color="primary"
                >
                  Contact us
                </LinkButton>
              </Column>
              <Column
                style={{ paddingTop: '20px' }}
                image={
                  <StaticImage
                    style={{ marginBottom: '16px' }}
                    src="../images/icons/hands-raised.svg"
                    alt="hands raised"
                    placeholder="blurred"
                  />
                }
              >
                <Typography variant="h4">
                  <strong>Ready to become a DISH customer?</strong>
                </Typography>
                <Typography className="center-text" variant="body">
                  Welcome to the family—now let’s see what’s available in your
                  area first!
                </Typography>
                <AvailabilityForm
                  buttonColor="primary"
                  buttonText="Find"
                  placeholderText="Zip code"
                />
              </Column>
            </Columns>
          </Stack>
        </VariableContent>

        <SplitContent
          backgroundColor="light"
          image={
            <StaticImage
              src="../images/lifestyle/party.webp"
              alt="people laughing at show together"
              placeholder="blurred"
            />
          }
          mainContent={
            <>
              <Typography variant="h2">
                LOCAL CHANNELS MAKE DISH YOUR NEIGHBORHOOD WATCH
              </Typography>
              <Typography variant="body">
                All DISH packages in {City} come with local channels so you can
                keep up with your local sports team or your favorite news
                channel. You’ll also have access to three months FREE of premium
                channels like Showtime and the DISH Movie Pack.*
              </Typography>
              <Brandy
                text={data.disclaimerFour.text}
                variant="legal"
                style={{
                  textAlign: 'center',
                }}
              />
            </>
          }
        />

        <VariableContent
          mainContent={
            <>
              <Typography variant="h2">
                Why Get DISH Network Near Me?
              </Typography>
            </>
          }
        >
          <Stack>
            <Columns className="center-text info-cards">
              <Column
                style={{ paddingTop: '20px' }}
                image={
                  <StaticImage
                    src="../images/icons/bill.svg"
                    alt=""
                    placeholder="blurred"
                  />
                }
              >
                <Typography className="center-text" variant="h4">
                  <strong>2-year TV price guarantee</strong>
                </Typography>
                <Typography className="center-text" variant="body">
                  The only provider that guarantees your monthly TV price for
                  two years
                </Typography>
              </Column>
              <Column
                style={{ paddingTop: '20px' }}
                image={
                  <StaticImage
                    src="../images/icons/van.svg"
                    alt=""
                    placeholder="blurred"
                  />
                }
              >
                <Typography className="center-text" variant="h4">
                  <strong>Free installation</strong>
                </Typography>
                <Typography className="center-text" variant="body">
                  Kick back and let the pros handle your DISH Network
                  installation.
                </Typography>
              </Column>
              <Column
                style={{ paddingTop: '20px' }}
                image={
                  <StaticImage
                    src="../images/icons/shield.svg"
                    alt=""
                    placeholder="blurred"
                  />
                }
              >
                <Typography className="center-text" variant="h4">
                  <strong>Signal Reliability Guarantee</strong>
                </Typography>
                <Typography className="center-text" variant="body">
                  A reliable signal or your money back.
                </Typography>
              </Column>
            </Columns>
          </Stack>
        </VariableContent>

        <SplitContent
          backgroundColor="light"
          image={
            <StaticImage
              src="../images/lifestyle/games-dvr.webp"
              alt="dvr and tv"
              placeholder="blurred"
            />
          }
          mainContent={
            <>
              <Typography variant="h2">
                SCORE BIG WITH DISH SPORTS PACKAGES
              </Typography>
              <Typography variant="h5">
                Catch every home run, slam dunk, and touchdown with a DISH
                sports package. From college sports to professional leagues,
                DISH sports packages get you in the game without ever leaving
                your couch.
              </Typography>
              {isCaliforniaPage ? (
                <PhoneCTA color="primary" staticPhoneNumber="855-662-4704">
                  Call to order!
                </PhoneCTA>
              ) : (
                <PhoneCTA color="primary">Call to order!</PhoneCTA>
              )}
            </>
          }
        />

        <SplitContent
          image={
            <StaticImage
              src="../images/lifestyle/family-time.webp"
              alt="Devices"
              placeholder="blurred"
            />
          }
          mainContent={
            <>
              <Typography variant="h2" className="uppercase">
                BRING INTERNET AND TV TOGETHER IN {City}
              </Typography>
              <Typography variant="body">
                DISH does TV, but we also work with internet providers to bring
                the complete entertainment experience into your home in {City}.
                Maximize your deals and save big when you bundle your TV with
                internet partners like Hughesnet and Frontier.
              </Typography>
            </>
          }
        />

        <VariableContent
          backgroundColor="dark"
          mainContent={
            <>
              <Typography variant="h2">Looking for DISH in {State}?</Typography>
              <Dropdown
                options={cityDropdownData}
                onChange={(e) => navigate(e.value)}
                placeholderText="Select City"
                aoa=""
              />
              {cityDropdownData?.map((current) => (
                <StyledLink
                  href={withPrefix(current.value)}
                  key={current.value}
                >
                  {current.label}
                </StyledLink>
              ))}
            </>
          }
        ></VariableContent>

        {/* only show based on dsm entry of Customer_Service being Yes (CA, FL, GA, and TX) */}
        {Customer_Service && (
          <VariableContent
            mainContent={
              <>
                <Typography variant="h2">
                  DISH Network Customer Service Numbers & Hours
                </Typography>
                <Typography variant="body">
                  Call DISH now to place an order or get DISH Network TV
                  support.
                </Typography>
              </>
            }
            alignMainContent="Center"
            alignImageToBottom={false}
            centeredContent={false}
            backgroundColor={null}
            breadcrumbProps={null}
            customLink={null}
          >
            <Stack>
              <Columns leftAligned={false} className="center-text info-cards">
                <Column
                  style={{ paddingTop: '20px' }}
                  image={
                    <StaticImage
                      style={{ marginBottom: '16px' }}
                      src="../images/icons/customer-service.svg"
                      alt="customer service agent icon"
                      placeholder="blurred"
                    />
                  }
                >
                  <Typography className="center-text" variant="h4">
                    <strong>New Orders</strong>
                  </Typography>
                  <Typography className="center-text" variant="body">
                    6am–8pm MT, M–F
                  </Typography>
                  <Typography className="center-text" variant="body">
                    Español 7am–7pm MT, M–F
                  </Typography>
                  {isCaliforniaPage ? (
                    <PhoneCTA
                      color="primary"
                      staticPhoneNumber="855-662-4704"
                    ></PhoneCTA>
                  ) : (
                    <PhoneCTA color="primary" />
                  )}
                </Column>
                <Column
                  style={{ paddingTop: '20px' }}
                  image={
                    <StaticImage
                      style={{ marginBottom: '16px' }}
                      src="../images/icons/red-van.svg"
                      alt="red van icon"
                      placeholder="blurred"
                    />
                  }
                >
                  <Typography className="center-text" variant="h4">
                    <strong>Customer Care</strong>
                  </Typography>
                  <Typography className="center-text" variant="body">
                    Technical support: available 24/7
                  </Typography>
                  <Typography className="center-text" variant="body">
                    Account help: 6am–10pm MT daily
                  </Typography>
                  <LinkButton
                    to="tel:8003333474"
                    target="_blank"
                    color="primary"
                    leftIcon={<Phone />}
                  >
                    1-800-333-3474
                  </LinkButton>
                </Column>
              </Columns>
            </Stack>
          </VariableContent>
        )}

        <VariableContent backgroundColor="light">
          <Typography variant="h2">Frequently Asked Questions</Typography>
          <Accordion
            items={[
              {
                title: getFAQ(FAQ_1)?.question,
                content: getFAQ(FAQ_1)?.answer,
              },
              {
                title: getFAQ(FAQ_2)?.question,
                content: getFAQ(FAQ_2)?.answer,
              },
              {
                title: getFAQ(FAQ_3)?.question,
                content: getFAQ(FAQ_3)?.answer,
              },
              {
                title: getFAQ(FAQ_4)?.question,
                content: getFAQ(FAQ_4)?.answer,
              },
              {
                title: getFAQ(FAQ_5)?.question,
                content: getFAQ(FAQ_5)?.answer,
              },
              {
                title: getFAQ(FAQ_6)?.question,
                content: getFAQ(FAQ_6)?.answer,
              },
            ]}
          />{' '}
        </VariableContent>
      </>
    ),
    disableBreadcrumbs: false,
  }

  return <Layout {...layoutProps} />
}

const StyledLink = styled.a`
  display: none !important;
`
export default City

export const query = graphql`
  query CityPageQuery($pagePath: String, $stateAbbreviation: String) {
    allBrandyPackage {
      edges {
        node {
          brandy_id
          bullets {
            text
          }
          price {
            amount
            duration
          }
          brand_token
          name
          id
        }
      }
    }
    pageInfo: allDatasetManagerDishAoa(
      filter: { Page_Path: { eq: $pagePath } }
    ) {
      edges {
        node {
          City
          FAQ_1
          FAQ_2
          FAQ_3
          FAQ_4
          FAQ_5
          FAQ_6
          Page_Path
          State
          Region
          Meta_Description
          Meta_Title
          Promo_Code
          Customer_Service
        }
      }
    }
    states: allDatasetManagerDishAoa(filter: { Area_Type: { eq: "state" } }) {
      edges {
        node {
          State
          Page_Path
          Region
        }
      }
    }
    cities: allDatasetManagerDishAoa(
      filter: { State: { eq: $stateAbbreviation }, Area_Type: { eq: "city" } }
    ) {
      edges {
        node {
          City
          Page_Path
        }
      }
    }
    disclaimerOne: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-premium-short" }
    ) {
      id
      text
    }
    disclaimerTwo: brandyDisclaimer(brandy_id: { eq: "disclaimer-dnfooter1" }) {
      id
      text
    }
    disclaimerThree: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-googleasst" }
    ) {
      brandy_id
      symbol
      text
    }
    disclaimerFour: brandyDisclaimer(brandy_id: { eq: "disclaimer-premium" }) {
      brandy_id
      symbol
      text
    }
  }
`
